import Constants from '../Constants.js';
import RuleReducer from './RuleReducer.js';

/**
 * This is meant to be a generic, reusable user action generator.
 * It needs the resourceId.
 * <code>
 * rule : ( ruleId ) => {
 *   return new Rule( {
 *     ruleId,
 *     resourceId : this.id, // with is th device, system, project, script, etc id
 *     resourceApi : ( dispatch, getState, opts, resourceId ) => {
 *       // this return's a the api on which the user's calls exist
 *       return opts.api.resource( resourceId )
 *     }
 *   })
 * }
 */
class Rule {
  constructor({
    ruleId,
    resourceId,
    resourceApi
  }) {
    this.resourceApi = resourceApi;
    this.resourceId = resourceId;
    this.ruleId = ruleId;
  }
  createAction(method, status, func) {
    return (dispatch, getState, opts) => {
      dispatch({
        type: Constants.RULE_STATUS,
        resourceId: this.resourceId,
        ruleId: this.ruleId,
        status
      });
      const api = this.resourceApi(dispatch, getState, opts, this.resourceId);
      // override top level api with resource api
      return func(dispatch, getState, {
        ...opts,
        api
      }, this.resourceId).catch(error => {
        dispatch({
          type: Constants.RULE_STATUS,
          resourceId: this.resourceId,
          ruleId: this.ruleId,
          status: `${method}Error`,
          error
        });
        return Promise.reject(error);
      });
    };
  }
  get(opts) {
    return this.createAction('load', 'loading', async (dispatch, getState, {
      api
    }, resourceId) => {
      const {
        requestId,
        ...reqOpts
      } = opts || {};
      const rule = await api.rule(this.ruleId).get(reqOpts);
      dispatch({
        type: Constants.RULE_UPDATE,
        resourceId,
        rule,
        requestId
      });
      return rule;
    });
  }
  update(opts) {
    return this.createAction('update', 'updating', async (dispatch, getState, {
      api
    }, resourceId) => {
      const {
        requestId,
        ...reqOpts
      } = opts || {};
      const rule = await api.rule(this.ruleId).update(reqOpts);
      dispatch({
        type: Constants.RULE_UPDATE,
        resourceId,
        rule,
        requestId
      });
      return rule;
    });
  }
  delete(opts) {
    return this.createAction('delete', 'deleting', async (dispatch, getState, {
      api
    }, resourceId) => {
      const {
        requestId,
        ...reqOpts
      } = opts || {};
      const r = await api.rule(this.ruleId).delete(reqOpts);
      dispatch({
        type: Constants.RULE_REMOVE,
        resourceId,
        ruleId: this.ruleId,
        requestId
      });
      return r;
    });
  }

  /**
   * Retrieves this object from the redux store. Equivalent to
   *  RuleReducer.getRule( state, resourceId, ruleId )
   * @param {Object} state The redux store
   */
  getRule(state) {
    return RuleReducer.getRule(state, this.resourceId, this.ruleId);
  }

  /**
   * Retrieves this object's status from the redux store. Equivalent to 
   * RuleReducer.getStatus( state, resourceId, ruleId )
   * @param {Object} state The redux store
   */
  getStatus(state) {
    return RuleReducer.getStatus(state, this.resourceId, this.ruleId);
  }

  /**
   * Retrieves this object's error from the redux store. Equivalent to 
   * RuleReducer.getStatus( state, resourceId, ruleId )
   * @param {Object} state The redux store
   */
  getError(state) {
    return RuleReducer.getError(state, this.resourceId, this.ruleId);
  }
}
export default Rule;